import React, { useState, useEffect, useRef, useCallback } from "react";
import type {
  SrdMonster, SrdSpell, SrdCondition,
  SrdEquipmentItem, SrdMagicItem, SrdClass, SrdRace,
  SrdSpeed, SrdSenses, SrdFeature as SrdMonsterFeature,
} from "@roll1/shared/srd/types";
import { formatCr } from "@roll1/shared/srd/mapping";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useCanvasContext } from "../../../contexts/CanvasContext";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { ScrollArea } from "../../../components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { useI18n } from "../../../i18n";
import { ruMonsterName } from "@roll1/shared/srd/ru-names";
import type { TranslationKeys } from "../../../i18n";
import SidebarPanel from "../../../components/ui/SidebarPanel";
import { SidebarTab } from "../../../components/ui/panel-header";
import { ListFinder, ListRow } from "../../../components/ui/row";
import { IconButton } from "../../../components/ui/icon-button";
import type { MenuEntry } from "../../../components/ui/row-menu";
import { BookOpen, FileText, MapPin } from "lucide-react";

type Tab = "monsters" | "spells" | "conditions" | "equipment" | "magic-items" | "classes" | "races";

interface ApiPage<T> { total: number; limit: number; offset: number; data: T[]; }

// ─── Constants ───

const TABS: { id: Tab; labelKey: keyof TranslationKeys }[] = [
  { id: "monsters", labelKey: "srd.monsters" },
  { id: "spells", labelKey: "srd.spells" },
  { id: "classes", labelKey: "srd.classes" },
  { id: "races", labelKey: "srd.races" },
  { id: "equipment", labelKey: "srd.equipment" },
  { id: "magic-items", labelKey: "srd.magicItems" },
  { id: "conditions", labelKey: "srd.conditions" },
];

const CR_OPTIONS = [
  "0", "1/8", "1/4", "1/2",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10",
  "11", "12", "13", "14", "15", "16", "17", "20", "21", "23", "24", "30",
];
const CR_MAP: Record<string, string> = { "1/8": "0.125", "1/4": "0.25", "1/2": "0.5" };

const MONSTER_TYPES = [
  "aberration", "beast", "celestial", "construct", "dragon",
  "elemental", "fey", "fiend", "giant", "humanoid",
  "monstrosity", "ooze", "plant", "undead",
];

const SPELL_SCHOOLS = [
  "Abjuration", "Conjuration", "Divination", "Enchantment",
  "Evocation", "Illusion", "Necromancy", "Transmutation",
];

const SPELL_CLASSES = [
  "Bard", "Cleric", "Druid", "Paladin",
  "Ranger", "Sorcerer", "Warlock", "Wizard",
];

const EQUIPMENT_CATEGORIES = ["Weapon", "Armor", "Adventuring Gear", "Tools", "Mounts and Vehicles"];

const RARITY_OPTIONS = ["Common", "Uncommon", "Rare", "Very Rare", "Legendary"];

// ─── Hook: debounced search ───

function useDebouncedSearch() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const ref = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    ref.current = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(ref.current);
  }, [search]);
  return { search, setSearch, debounced };
}

// ─── Main component ───

/** П8: the first rows of a list, then «Показать ещё N»; the server gives 50 a page. */
const LIMIT = 12;
const PAGE = 50;

export default function SrdPanel({ homebrew }: { readonly homebrew?: React.ReactNode } = {}) {
  const { isGM, mp, gameSystem, outgoingPageId } = useSessionContext();
  const { engine } = useCanvasContext();
  const { t, locale } = useI18n();
  // GJ-22: the monster's name in the language of the table when the list has it (the SRD itself is English).
  const monsterName = (name: string): string => (locale === "ru" ? ruMonsterName(name) ?? name : name);

  const onSpawnMonster = useCallback((slug: string, withSheet: boolean, name?: string) => {
    if (!mp.session) return;
    const gs = engine.gridSize; const x = 5 * gs + gs / 2; const y = 5 * gs + gs / 2;
    // The map the GM looks at, not the players' one.
    mp.send({ type: 'srd:spawn-monster', slug, x, y, withSheet, ...(name !== undefined ? { name } : {}), pageId: outgoingPageId });
  }, [mp, engine.gridSize, outgoingPageId]);
  const [tab, setTab] = useState<Tab>("monsters");

  // Filters
  const { search, setSearch, debounced } = useDebouncedSearch();
  const [crFilter, setCrFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [eqCategoryFilter, setEqCategoryFilter] = useState("");
  const [rarityFilter, setRarityFilter] = useState("");

  // Data
  const [monsters, setMonsters] = useState<SrdMonster[]>([]);
  const [spells, setSpells] = useState<SrdSpell[]>([]);
  const [conditions, setConditions] = useState<SrdCondition[]>([]);
  const [equipment, setEquipment] = useState<SrdEquipmentItem[]>([]);
  const [magicItems, setMagicItems] = useState<SrdMagicItem[]>([]);
  const [classesList, setClassesList] = useState<{ slug: string; name: string; hitDie: number; savingThrows: string[]; subclass: string | null }[]>([]);
  const [racesList, setRacesList] = useState<{ slug: string; name: string; speed: number; size: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [shown, setShown] = useState(LIMIT);

  // Detail views
  const [selectedMonster, setSelectedMonster] = useState<SrdMonster | null>(null);
  const [selectedSpell, setSelectedSpell] = useState<SrdSpell | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<SrdCondition | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<SrdEquipmentItem | null>(null);
  const [selectedMagicItem, setSelectedMagicItem] = useState<SrdMagicItem | null>(null);
  const [selectedClass, setSelectedClass] = useState<SrdClass | null>(null);
  const [selectedRace, setSelectedRace] = useState<SrdRace | null>(null);

  // Reset on tab switch
  useEffect(() => {
    setSearch("");
    setCrFilter(""); setTypeFilter("");
    setLevelFilter(""); setSchoolFilter(""); setClassFilter("");
    setEqCategoryFilter(""); setRarityFilter("");
    setSelectedMonster(null); setSelectedSpell(null);
    setSelectedCondition(null); setSelectedEquipment(null);
    setSelectedMagicItem(null); setSelectedClass(null); setSelectedRace(null);
    setOffset(0);
    setShown(LIMIT);
  }, [tab, setSearch]);

  // Reset offset when search or filters change
  useEffect(() => {
    setOffset(0);
    setShown(LIMIT);
  }, [debounced, crFilter, typeFilter, levelFilter, schoolFilter, classFilter, eqCategoryFilter, rarityFilter]);

  // ─── Fetch data ───

  useEffect(() => {
    if (tab !== "monsters") return;
    setLoading(true);
    const p = new URLSearchParams();
    if (debounced) p.set("search", debounced);
    if (crFilter) p.set("cr", CR_MAP[crFilter] ?? crFilter);
    if (typeFilter) p.set("type", typeFilter);
    p.set("limit", "50");
    p.set("offset", String(offset));
    fetch(`/api/srd/monsters?${p}`).then(r => r.json())
      .then((d: ApiPage<SrdMonster>) => {
        if (offset > 0) setMonsters(prev => [...prev, ...d.data]);
        else setMonsters(d.data);
        setTotal(d.total);
      })
      .catch(() => { if (offset === 0) { setMonsters([]); setTotal(0); } })
      .finally(() => setLoading(false));
  }, [tab, debounced, crFilter, typeFilter, offset]);

  useEffect(() => {
    if (tab !== "spells") return;
    setLoading(true);
    const p = new URLSearchParams();
    if (debounced) p.set("search", debounced);
    if (levelFilter) p.set("level", levelFilter);
    if (schoolFilter) p.set("school", schoolFilter);
    if (classFilter) p.set("class", classFilter);
    p.set("limit", "50");
    p.set("offset", String(offset));
    fetch(`/api/srd/spells?${p}`).then(r => r.json())
      .then((d: ApiPage<SrdSpell>) => {
        if (offset > 0) setSpells(prev => [...prev, ...d.data]);
        else setSpells(d.data);
        setTotal(d.total);
      })
      .catch(() => { if (offset === 0) { setSpells([]); setTotal(0); } })
      .finally(() => setLoading(false));
  }, [tab, debounced, levelFilter, schoolFilter, classFilter, offset]);

  useEffect(() => {
    if (tab !== "conditions") return;
    setLoading(true);
    const p = new URLSearchParams();
    if (debounced) p.set("search", debounced);
    p.set("limit", "50");
    p.set("offset", String(offset));
    fetch(`/api/srd/conditions?${p}`).then(r => r.json())
      .then((d: ApiPage<SrdCondition>) => {
        if (offset > 0) setConditions(prev => [...prev, ...d.data]);
        else setConditions(d.data);
        setTotal(d.total);
      })
      .catch(() => { if (offset === 0) { setConditions([]); setTotal(0); } })
      .finally(() => setLoading(false));
  }, [tab, debounced, offset]);

  useEffect(() => {
    if (tab !== "equipment") return;
    setLoading(true);
    const p = new URLSearchParams();
    if (debounced) p.set("search", debounced);
    if (eqCategoryFilter) p.set("category", eqCategoryFilter);
    p.set("limit", "50");
    p.set("offset", String(offset));
    fetch(`/api/srd/equipment?${p}`).then(r => r.json())
      .then((d: ApiPage<SrdEquipmentItem>) => {
        if (offset > 0) setEquipment(prev => [...prev, ...d.data]);
        else setEquipment(d.data);
        setTotal(d.total);
      })
      .catch(() => { if (offset === 0) { setEquipment([]); setTotal(0); } })
      .finally(() => setLoading(false));
  }, [tab, debounced, eqCategoryFilter, offset]);

  useEffect(() => {
    if (tab !== "magic-items") return;
    setLoading(true);
    const p = new URLSearchParams();
    if (debounced) p.set("search", debounced);
    if (rarityFilter) p.set("rarity", rarityFilter);
    p.set("limit", "50");
    p.set("offset", String(offset));
    fetch(`/api/srd/magic-items?${p}`).then(r => r.json())
      .then((d: ApiPage<SrdMagicItem>) => {
        if (offset > 0) setMagicItems(prev => [...prev, ...d.data]);
        else setMagicItems(d.data);
        setTotal(d.total);
      })
      .catch(() => { if (offset === 0) { setMagicItems([]); setTotal(0); } })
      .finally(() => setLoading(false));
  }, [tab, debounced, rarityFilter, offset]);

  useEffect(() => {
    if (tab !== "classes") return;
    setLoading(true);
    fetch("/api/srd/classes").then(r => r.json())
      .then((d: { total: number; data: typeof classesList }) => { setClassesList(d.data); setTotal(d.total); })
      .catch(() => { setClassesList([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    if (tab !== "races") return;
    setLoading(true);
    fetch("/api/srd/races").then(r => r.json())
      .then((d: { total: number; data: typeof racesList }) => { setRacesList(d.data); setTotal(d.total); })
      .catch(() => { setRacesList([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [tab]);

  const handleBack = useCallback(() => {
    setSelectedMonster(null); setSelectedSpell(null);
    setSelectedCondition(null); setSelectedEquipment(null);
    setSelectedMagicItem(null); setSelectedClass(null); setSelectedRace(null);
  }, []);

  const fetchClassDetail = useCallback((slug: string) => {
    fetch(`/api/srd/classes/${slug}`).then(r => r.json())
      .then((c: SrdClass) => setSelectedClass(c));
  }, []);

  const fetchRaceDetail = useCallback((slug: string) => {
    fetch(`/api/srd/races/${slug}`).then(r => r.json())
      .then((r: SrdRace) => setSelectedRace(r));
  }, []);

  // ─── Non-D&D fallback ───

  if (gameSystem !== 'dnd5e') {
    return (
      <SidebarTab title={t('srd.title')} tab="srd">
        {homebrew}
        <div className="p-4 text-center text-muted-foreground text-ui">
          {t('srd.unavailable')}
        </div>
      </SidebarTab>
    );
  }

  // ─── Detail views (under the tab's header) ───

  const detail =
    selectedMonster ? <MonsterDetail monster={selectedMonster} isGM={isGM} onBack={handleBack} onSpawn={onSpawnMonster} />
    : selectedSpell ? <SpellDetail spell={selectedSpell} onBack={handleBack} />
    : selectedCondition ? <ConditionDetail condition={selectedCondition} onBack={handleBack} />
    : selectedEquipment ? <EquipmentDetail item={selectedEquipment} onBack={handleBack} />
    : selectedMagicItem ? <MagicItemDetail item={selectedMagicItem} onBack={handleBack} />
    : selectedClass ? <ClassDetail cls={selectedClass} onBack={handleBack} />
    : selectedRace ? <RaceDetail race={selectedRace} onBack={handleBack} />
    : null;
  if (detail !== null) {
    return (
      <SidebarTab title={t('srd.title')} tab="srd">
        {detail}
      </SidebarTab>
    );
  }

  // ─── List view (sidebar polish П8, mockup tableSrd()): the search and the category chips, Homebrew, the list ───

  const tabLabel = TABS.find((d) => d.id === tab)!.labelKey;
  const loaded =
    tab === "monsters" ? monsters.length :
    tab === "spells" ? spells.length :
    tab === "conditions" ? conditions.length :
    tab === "equipment" ? equipment.length :
    tab === "magic-items" ? magicItems.length :
    tab === "classes" ? classesList.length : racesList.length;
  const paginated = tab !== "classes" && tab !== "races";
  // 12 rows, then «Показать ещё N» (П8) — the next 50 come from the server when the loaded ones run out.
  const rest = Math.max(0, (paginated ? total : loaded) - shown);
  const showMore = (): void => {
    const next = shown + PAGE;
    setShown(next);
    if (paginated && next > loaded && loaded < total) setOffset(loaded);
  };
  const cap = <T,>(items: readonly T[]): readonly T[] => items.slice(0, shown);
  const spawnMenu = (m: SrdMonster): MenuEntry[] => [
    { label: t('sb.srd.open'), icon: BookOpen, onSelect: () => setSelectedMonster(m) },
    { label: t('sb.srd.place'), icon: MapPin, onSelect: () => onSpawnMonster(m.slug, false, monsterName(m.name)), hidden: !isGM },
    { label: t('sb.srd.placeWithSheet'), icon: FileText, onSelect: () => onSpawnMonster(m.slug, true, monsterName(m.name)), hidden: !isGM },
  ];
  const openRow = (key: string, label: string, meta: string | undefined, tip: string, open: () => void): React.JSX.Element => (
    <ListRow key={key} className="srd-item" label={label} meta={meta} tip={tip} onOpen={open} />
  );

  return (
    <SidebarTab title={t('srd.title')} tab="srd">
      <ListFinder
        value={tab === "classes" || tab === "races" ? "" : search}
        onChange={setSearch}
        placeholder={t('sb.srd.find')}
        chips={TABS.map((d) => ({ id: d.id, label: t(d.labelKey) }))}
        chip={tab}
        onChip={(id) => setTab(id as Tab)}
        testid="srd-search"
      />

      {/* Filters of the category: kept from the old panel, one compact row */}
      {tab === "monsters" || tab === "spells" || tab === "equipment" || tab === "magic-items" ? (
        <div className="srd-filters">
          {tab === "monsters" && (
            <>
              <Select value={crFilter} onValueChange={v => setCrFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder="CR" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('srd.allCR')}</SelectItem>
                  {CR_OPTIONS.map(cr => <SelectItem key={cr} value={cr}>CR {cr}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={v => setTypeFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder={t('srd.filterType')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('srd.allTypes')}</SelectItem>
                  {MONSTER_TYPES.map(mt => <SelectItem key={mt} value={mt}>{mt}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {tab === "spells" && (
            <>
              <Select value={levelFilter} onValueChange={v => setLevelFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder={t('srd.filterLevel')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('srd.allLevels')}</SelectItem>
                  <SelectItem value="0">{t('srd.cantrip')}</SelectItem>
                  {[1,2,3,4,5,6,7,8,9].map(l => <SelectItem key={l} value={String(l)}>{t('srd.level')} {l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={schoolFilter} onValueChange={v => setSchoolFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder={t('srd.filterSchool')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('srd.allSchools')}</SelectItem>
                  {SPELL_SCHOOLS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={classFilter} onValueChange={v => setClassFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder={t('srd.filterClass')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('srd.allClasses')}</SelectItem>
                  {SPELL_CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {tab === "equipment" && (
            <Select value={eqCategoryFilter} onValueChange={v => setEqCategoryFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder={t('srd.filterCategory')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('srd.allCategories')}</SelectItem>
                {EQUIPMENT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {tab === "magic-items" && (
            <Select value={rarityFilter} onValueChange={v => setRarityFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-7 text-meta flex-1"><SelectValue placeholder={t('srd.filterRarity')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('srd.allRarities')}</SelectItem>
                {RARITY_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : null}

      {homebrew}

      <SidebarPanel id={`srd.${tab}`} title={t(tabLabel)} count={loading && loaded === 0 ? '…' : (paginated ? total : loaded)} tight testid="srd-list">
        <ul className="flex flex-col">
          {tab === "monsters" && cap(monsters).map(m => {
            const name = monsterName(m.name);
            return (
              <ListRow
                key={m.slug}
                className="srd-item"
                data-testid="srd-monster"
                label={name}
                meta={`CR ${formatCr(m.cr)}`}
                tip={`${name} · CR ${formatCr(m.cr)} · ${m.size} ${m.type}`}
                onOpen={() => setSelectedMonster(m)}
                draggable
                onDragStart={(e: React.DragEvent) => {
                  const payload = {
                    dragType: "srd-monster" as const,
                    slug: m.slug,
                    name,
                    type: m.type,
                    size: m.size,
                    hp: m.hp,
                    ac: m.ac,
                    cr: m.cr,
                    tokenSize: m.tokenSize,
                  };
                  e.dataTransfer.setData("text/plain", m.name);
                  e.dataTransfer.setData("application/json", JSON.stringify(payload));
                  e.dataTransfer.effectAllowed = "copy";
                }}
                actions={isGM ? (
                  <IconButton size="sm" label={t('sb.srd.place')} icon={<MapPin size={14} aria-hidden="true" />} data-testid="srd-place" onClick={() => onSpawnMonster(m.slug, false, name)} />
                ) : undefined}
                menu={spawnMenu(m)}
              />
            );
          })}
          {tab === "spells" && cap(spells).map(s => {
            const level = s.level === 0 ? t('srd.cantrip') : `${t('srd.level')} ${s.level}`;
            return openRow(s.slug, s.name, s.concentration ? `${level} · ${t('srd.concentrationAbbr')}` : level, `${s.name} · ${level} · ${s.school}`, () => setSelectedSpell(s));
          })}
          {tab === "conditions" && cap(conditions).map(c => openRow(c.slug, c.name, undefined, c.name, () => setSelectedCondition(c)))}
          {tab === "equipment" && cap(equipment).map(e => openRow(e.slug, e.name, e.cost || undefined, `${e.name} · ${e.category}${e.cost ? ` · ${e.cost}` : ""}`, () => setSelectedEquipment(e)))}
          {tab === "magic-items" && cap(magicItems).map(i => openRow(i.slug, i.name, i.rarity, `${i.name} · ${i.rarity}`, () => setSelectedMagicItem(i)))}
          {tab === "classes" && cap(classesList).map(c => openRow(c.slug, c.name, `d${c.hitDie}`, `${c.name} · d${c.hitDie} · ${c.savingThrows.join(", ")}`, () => fetchClassDetail(c.slug)))}
          {tab === "races" && cap(racesList).map(r => openRow(r.slug, r.name, `${r.speed} ft.`, `${r.name} · ${r.size} · ${r.speed} ft.`, () => fetchRaceDetail(r.slug)))}
        </ul>
        {!loading && total === 0 && loaded === 0 ? <p className="sb-line py-1 text-meta text-dim">{t('sb.nothingFound')}</p> : null}
        {rest > 0 && !(loading && shown > loaded) ? (
          <button type="button" className="list-more" onClick={showMore} data-testid="srd-load-more">
            {t('sb.showMore').replace('{n}', String(Math.min(rest, PAGE)))}
            <span className="text-dim"> · {Math.min(shown, paginated ? total : loaded)}/{paginated ? total : loaded}</span>
          </button>
        ) : null}
        {loading && loaded > 0 ? <p className="sb-line py-1 text-meta text-dim">{t('srd.loading')}</p> : null}
      </SidebarPanel>
    </SidebarTab>
  );
}

// ══════════════════════════════════════════════
// Detail components
// ══════════════════════════════════════════════

// ─── Monster Detail ───

function MonsterDetail({ monster: m, isGM, onBack, onSpawn }: {
  monster: SrdMonster; isGM: boolean; onBack: () => void;
  onSpawn: (slug: string, withSheet: boolean, name?: string) => void;
}) {
  const { t, locale } = useI18n();
  const monsterName = (name: string): string => (locale === "ru" ? ruMonsterName(name) ?? name : name);
  const abilityMod = (score: number) => {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : String(mod);
  };

  return (
    <div className="srd-panel">
      <div className="srd-detail-header">
        <button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button>
        {isGM && (
          <div className="srd-spawn-btns">
            <Button size="sm" variant="default" onClick={() => onSpawn(m.slug, false, monsterName(m.name))}>{t('srd.token')}</Button>
            <Button size="sm" variant="secondary" onClick={() => onSpawn(m.slug, true, monsterName(m.name))}>{t('srd.plusSheet')}</Button>
          </div>
        )}
      </div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{m.name}</h3>
          <p className="srd-detail__subtitle">{m.size} {m.type}{m.subtype ? ` (${m.subtype})` : ""}, {m.alignment}</p>
          <div className="srd-stat-block">
            <StatRow label="AC">{m.ac}{m.acType ? ` (${m.acType})` : ""}</StatRow>
            <StatRow label="HP">{m.hp} ({m.hitDice})</StatRow>
            <StatRow label="Speed">{formatSpeed(m.speed)}</StatRow>
          </div>
          <div className="srd-abilities">
            {(["str","dex","con","int","wis","cha"] as const).map(ab => (
              <div key={ab} className="srd-ability">
                <span className="srd-ability__label">{ab.toUpperCase()}</span>
                <span className="srd-ability__score">{m.abilities[ab]}</span>
                <span className="srd-ability__mod">({abilityMod(m.abilities[ab])})</span>
              </div>
            ))}
          </div>
          {m.savingThrows && Object.keys(m.savingThrows).length > 0 && (
            <StatRow label="Saves">{Object.entries(m.savingThrows).map(([k,v]) => `${k.toUpperCase()} +${v}`).join(", ")}</StatRow>
          )}
          {m.skills && Object.keys(m.skills).length > 0 && (
            <StatRow label="Skills">{Object.entries(m.skills).map(([k,v]) => `${capitalize(k)} +${v}`).join(", ")}</StatRow>
          )}
          {m.damageResistances && m.damageResistances.length > 0 && <StatRow label={t('srd.resistances')}>{m.damageResistances.join(", ")}</StatRow>}
          {m.damageImmunities && m.damageImmunities.length > 0 && <StatRow label={t('srd.immunities')}>{m.damageImmunities.join(", ")}</StatRow>}
          {m.conditionImmunities && m.conditionImmunities.length > 0 && <StatRow label="Cond. Imm.">{m.conditionImmunities.join(", ")}</StatRow>}
          <StatRow label={t('srd.senses')}>{formatSenses(m.senses)}</StatRow>
          {m.languages && <StatRow label={t('srd.languages')}>{m.languages}</StatRow>}
          <StatRow label={t('srd.cr')}>{formatCr(m.cr)} ({m.xp.toLocaleString()} XP)</StatRow>
          {m.traits && m.traits.length > 0 && <FeatureSection title={t('srd.traits')} features={m.traits} />}
          {m.actions && m.actions.length > 0 && <FeatureSection title={t('srd.actions')} features={m.actions} />}
          {m.reactions && m.reactions.length > 0 && <FeatureSection title={t('srd.reactions')} features={m.reactions} />}
          {m.legendaryActions && m.legendaryActions.length > 0 && <FeatureSection title={t('srd.legendaryActions')} features={m.legendaryActions} />}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Spell Detail ───

function SpellDetail({ spell: s, onBack }: { spell: SrdSpell; onBack: () => void }) {
  const { t } = useI18n();
  return (
    <div className="srd-panel">
      <div className="srd-detail-header"><button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button></div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{s.name}</h3>
          <p className="srd-detail__subtitle">
            {s.level === 0 ? `${s.school} cantrip` : `${ordinal(s.level)}-level ${s.school.toLowerCase()}`}
            {s.ritual && " (ritual)"}
          </p>
          <div className="srd-stat-block">
            <StatRow label={t('srd.castingTime')}>{s.castingTime}</StatRow>
            <StatRow label={t('srd.range')}>{s.range}</StatRow>
            <StatRow label={t('srd.components')}>{s.components}</StatRow>
            <StatRow label={t('srd.duration')}>{s.concentration ? "Concentration, " : ""}{s.duration}</StatRow>
          </div>
          <StatRow label={t('srd.classes')}>{s.classes.join(", ")}</StatRow>
          {s.damageDice && <StatRow label="Damage">{s.damageDice}{s.damageType ? ` ${s.damageType}` : ""}</StatRow>}
          {s.healDice && <StatRow label="Healing">{s.healDice}</StatRow>}
          <div className="srd-description">{s.description.split("\n").map((line, i) => <p key={i}>{line}</p>)}</div>
          {s.higherLevels && <div className="srd-higher-levels"><strong>At Higher Levels.</strong> {s.higherLevels}</div>}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Condition Detail ───

function ConditionDetail({ condition: c, onBack }: { condition: SrdCondition; onBack: () => void }) {
  const { t } = useI18n();
  return (
    <div className="srd-panel">
      <div className="srd-detail-header"><button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button></div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{c.name}</h3>
          <div className="srd-description">{c.description.split("\n").map((line, i) => <p key={i}>{line}</p>)}</div>
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Equipment Detail ───

function EquipmentDetail({ item: e, onBack }: { item: SrdEquipmentItem; onBack: () => void }) {
  const { t } = useI18n();
  return (
    <div className="srd-panel">
      <div className="srd-detail-header"><button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button></div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{e.name}</h3>
          <p className="srd-detail__subtitle">{e.category}</p>
          <div className="srd-stat-block">
            {e.cost && <StatRow label="Cost">{e.cost}</StatRow>}
            {e.weight != null && <StatRow label="Weight">{e.weight} lb.</StatRow>}
            {e.weaponCategory && <StatRow label="Weapon">{e.weaponCategory} {e.weaponRange}</StatRow>}
            {e.damageDice && <StatRow label="Damage">{e.damageDice}{e.damageType ? ` ${e.damageType}` : ""}</StatRow>}
            {e.twoHandedDice && <StatRow label="Two-Handed">{e.twoHandedDice}</StatRow>}
            {e.rangeNormal != null && <StatRow label="Range">{e.rangeNormal}{e.rangeLong ? `/${e.rangeLong}` : ""} ft.</StatRow>}
            {e.properties && e.properties.length > 0 && <StatRow label="Properties">{e.properties.join(", ")}</StatRow>}
            {e.armorCategory && <StatRow label="Armor">{e.armorCategory}</StatRow>}
            {e.acBase != null && <StatRow label="AC">{e.acBase}{e.acDexBonus ? " + Dex" : ""}{e.acMaxBonus ? ` (max ${e.acMaxBonus})` : ""}</StatRow>}
            {e.strMinimum != null && e.strMinimum > 0 && <StatRow label="Str Required">{e.strMinimum}</StatRow>}
            {e.stealthDisadvantage && <StatRow label="Stealth">Disadvantage</StatRow>}
          </div>
          {e.description && <div className="srd-description">{e.description.split("\n").map((line, i) => <p key={i}>{line}</p>)}</div>}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Magic Item Detail ───

function MagicItemDetail({ item: i, onBack }: { item: SrdMagicItem; onBack: () => void }) {
  const { t } = useI18n();
  return (
    <div className="srd-panel">
      <div className="srd-detail-header"><button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button></div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{i.name}</h3>
          <p className="srd-detail__subtitle">{i.rarity}{i.category ? ` · ${i.category}` : ""}</p>
          <div className="srd-description">{i.description.split("\n").map((line, idx) => <p key={idx}>{line}</p>)}</div>
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Class Detail ───

function ClassDetail({ cls: c, onBack }: { cls: SrdClass; onBack: () => void }) {
  const { t } = useI18n();
  const [showLevel, setShowLevel] = useState<number | null>(null);

  return (
    <div className="srd-panel">
      <div className="srd-detail-header"><button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button></div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{c.name}</h3>
          <div className="srd-stat-block">
            <StatRow label="Hit Die">d{c.hitDie}</StatRow>
            <StatRow label="Saving Throws">{c.savingThrows.join(", ")}</StatRow>
            <StatRow label="Proficiencies">{c.proficiencies.join(", ")}</StatRow>
            {c.skillChoices.length > 0 && (
              <StatRow label="Skills">Choose {c.skillChoices[0].choose} from: {c.skillChoices[0].from.join(", ")}</StatRow>
            )}
          </div>

          {c.subclass && (
            <div className="srd-feature-section">
              <h4 className="srd-feature-title">Subclass: {c.subclass.name}</h4>
              <div className="srd-description">{c.subclass.description.split("\n").map((l, i) => <p key={i}>{l}</p>)}</div>
            </div>
          )}

          {/* Level progression */}
          <div className="srd-feature-section">
            <h4 className="srd-feature-title">Levels</h4>
            <div className="srd-level-grid">
              {c.levels.map(l => (
                <button key={l.level} className={`srd-level-btn${showLevel === l.level ? " srd-level-btn--active" : ""}`}
                  onClick={() => setShowLevel(showLevel === l.level ? null : l.level)}>
                  {l.level}
                </button>
              ))}
            </div>
            {showLevel != null && (() => {
              const lvl = c.levels.find(l => l.level === showLevel);
              const feats = c.features.filter(f => f.level === showLevel);
              return (
                <div className="srd-level-detail">
                  <p className="srd-level-detail__prof">Proficiency Bonus: +{lvl?.proficiencyBonus}</p>
                  {lvl?.spellcasting && (
                    <div className="srd-level-detail__spell">
                      {lvl.spellcasting.cantripsKnown != null && <span>Cantrips: {lvl.spellcasting.cantripsKnown}</span>}
                      {lvl.spellcasting.spellsKnown != null && <span>Spells Known: {lvl.spellcasting.spellsKnown}</span>}
                      {Object.keys(lvl.spellcasting.slots).length > 0 && (
                        <span>Slots: {Object.entries(lvl.spellcasting.slots).map(([k,v]) => `${k}st:${v}`).join(", ")}</span>
                      )}
                    </div>
                  )}
                  {feats.map((f, i) => (
                    <div key={i} className="srd-feature">
                      <strong>{f.name}{f.subclass ? ` (${f.subclass})` : ""}.</strong>{" "}
                      <span>{f.description}</span>
                    </div>
                  ))}
                  {feats.length === 0 && <p className="srd-empty">{t('srd.noFeaturesAtLevel')}</p>}
                </div>
              );
            })()}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Race Detail ───

function RaceDetail({ race: r, onBack }: { race: SrdRace; onBack: () => void }) {
  const { t } = useI18n();
  return (
    <div className="srd-panel">
      <div className="srd-detail-header"><button className="srd-back" onClick={onBack}>&larr; {t('srd.back')}</button></div>
      <ScrollArea className="srd-detail-scroll">
        <div className="srd-detail">
          <h3 className="srd-detail__title">{r.name}</h3>
          <div className="srd-stat-block">
            <StatRow label="Speed">{r.speed} ft.</StatRow>
            <StatRow label="Size">{r.size}</StatRow>
            <StatRow label="Ability Bonuses">{r.abilityBonuses.map(b => `${b.ability.toUpperCase()} +${b.bonus}`).join(", ")}</StatRow>
            <StatRow label="Languages">{r.languages.join(", ")}</StatRow>
          </div>
          <div className="srd-description"><p>{r.age}</p></div>
          <div className="srd-description"><p>{r.alignment}</p></div>

          {r.traits.length > 0 && (
            <div className="srd-feature-section">
              <h4 className="srd-feature-title">Traits</h4>
              {r.traits.map((t, i) => (
                <div key={i} className="srd-feature">
                  <strong>{t.name}.</strong> <span>{t.description}</span>
                </div>
              ))}
            </div>
          )}

          {r.subraces.map((s, i) => (
            <div key={i} className="srd-feature-section">
              <h4 className="srd-feature-title">Subrace: {s.name}</h4>
              {s.abilityBonuses.length > 0 && (
                <StatRow label="Ability Bonuses">{s.abilityBonuses.map(b => `${b.ability.toUpperCase()} +${b.bonus}`).join(", ")}</StatRow>
              )}
              {s.traits.map((t, j) => (
                <div key={j} className="srd-feature">
                  <strong>{t.name}.</strong> <span>{t.description}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ══════════════════════════════════════════════
// Shared sub-components & helpers
// ══════════════════════════════════════════════

function StatRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="srd-stat-row">
      <span className="srd-stat-label">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function FeatureSection({ title, features }: { title: string; features: SrdMonsterFeature[] }) {
  return (
    <div className="srd-feature-section">
      <h4 className="srd-feature-title">{title}</h4>
      {features.map((f, i) => (
        <div key={i} className="srd-feature">
          <strong>{f.name}.</strong> <span>{f.description}</span>
        </div>
      ))}
    </div>
  );
}

function formatSpeed(speed: SrdSpeed): string {
  const parts: string[] = [`${speed.walk} ft.`];
  if (speed.fly) parts.push(`fly ${speed.fly} ft.${speed.hover ? " (hover)" : ""}`);
  if (speed.swim) parts.push(`swim ${speed.swim} ft.`);
  if (speed.climb) parts.push(`climb ${speed.climb} ft.`);
  if (speed.burrow) parts.push(`burrow ${speed.burrow} ft.`);
  return parts.join(", ");
}

function formatSenses(senses: SrdSenses): string {
  const parts: string[] = [];
  if (senses.darkvision) parts.push(`darkvision ${senses.darkvision} ft.`);
  if (senses.blindsight) parts.push(`blindsight ${senses.blindsight} ft.`);
  if (senses.tremorsense) parts.push(`tremorsense ${senses.tremorsense} ft.`);
  if (senses.truesight) parts.push(`truesight ${senses.truesight} ft.`);
  parts.push(`passive Perception ${senses.passivePerception}`);
  return parts.join(", ");
}

function capitalize(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

function ordinal(n: number): string {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
}

// ─── One SRD article by slug (the app-wide search opens it) ───

export type SrdArticleKind = "monster" | "spell" | "condition" | "equipment" | "magic-item";
const ARTICLE_PATH: Record<SrdArticleKind, string> = {
  monster: "monsters", spell: "spells", condition: "conditions", equipment: "equipment", "magic-item": "magic-items",
};

/**
 * The detail view the SRD panel shows, for one thing found by the search (GET /api/srd/<kind>/<slug> — reference
 * data). `onSpawn` — the GM's «token / + sheet» of a monster on the map the host chooses; absent — no spawn buttons.
 */
export function SrdArticle({ kind, slug, onBack, onSpawn }: {
  kind: SrdArticleKind; slug: string; onBack: () => void;
  onSpawn?: (slug: string, withSheet: boolean, name?: string) => void;
}) {
  const { t } = useI18n();
  const [data, setData] = useState<unknown>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    fetch(`/api/srd/${ARTICLE_PATH[kind]}/${encodeURIComponent(slug)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: unknown) => { if (alive) setData(d); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [kind, slug]);
  if (failed) return <div className="srd-panel p-4 text-ui text-muted-foreground" data-testid="srd-article-failed">{t("search.srd.failed")}</div>;
  if (data === null) return <div className="srd-panel p-4 text-ui text-muted-foreground">{t("search.srd.loading")}</div>;
  switch (kind) {
    case "monster": return <MonsterDetail monster={data as SrdMonster} isGM={onSpawn !== undefined} onBack={onBack} onSpawn={onSpawn ?? (() => undefined)} />;
    case "spell": return <SpellDetail spell={data as SrdSpell} onBack={onBack} />;
    case "condition": return <ConditionDetail condition={data as SrdCondition} onBack={onBack} />;
    case "equipment": return <EquipmentDetail item={data as SrdEquipmentItem} onBack={onBack} />;
    case "magic-item": return <MagicItemDetail item={data as SrdMagicItem} onBack={onBack} />;
  }
}
